import * as React from "react";
import { useState, useMemo } from "react";
import { projectSelector } from "@rflocal/shared";
import Button from "@scripts/react/components/Button";
import QRCode from "@scripts/react/components/QRCode";

interface QRCodeDisplayProps {
    workflowToken: string;
    workflowUrl?: string;
    darkMode?: boolean;
    size?: number;
    className?: string;
}

export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
    workflowToken,
    workflowUrl,
    darkMode = false,
    size = 200,
    className = ""
}) => {
    const [copied, setCopied] = useState(false);

    // Generate mobile URL
    const mobileUrl = useMemo(() => {
        if (!workflowToken) return "";
        const appUrl = projectSelector.appUrl();
        const baseUrl = `${appUrl}/workflows/mobile/${workflowToken}`;
        const url = new URL(baseUrl);
        // Only add dark param if user wants dark mode (light is default for mobile)
        if (darkMode) {
            url.searchParams.set("dark", "true");
        }
        return url.toString();
    }, [workflowToken, darkMode]);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(mobileUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    // Show loading state if no workflow token is provided
    if (!workflowToken) {
        return (
            <div className={`flex flex-col items-center gap-3 ${className}`}>
                <div
                    className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-3"
                    style={{ width: `${size}px`, height: `${size}px` }}
                >
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="far fa-spinner fa-spin" />
                        <span>Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col items-center gap-3 ${className}`}>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div
                    className="flex items-center justify-center"
                    style={{ width: `${size}px`, height: `${size}px` }}
                >
                    <QRCode data={mobileUrl} size={size} color={darkMode ? "#FFFFFF" : "#374151"} />
                </div>
            </div>

            {/* Copy link button */}
            <Button
                type="secondary"
                size="small"
                onClick={handleCopyLink}
                className="transition-colors"
            >
                <i className={`far ${copied ? "fa-check text-green-500" : "fa-copy"}`} />
                <span>{copied ? "Copied!" : "Copy Link"}</span>
            </Button>
        </div>
    );
};
